import { useEffect, useState } from 'react';
import EmbeddedCardLayout from '../../../components/embedded/CardLayout';
import AppTile, { type AppTileTag } from '../../../components/shared/AppTile';
import EmbeddedMoreAppsButton from '../../../components/embedded/MoreAppsButton';
import EmbeddedNeedHelpSection from '../../../components/embedded/NeedHelpSection';
import { useAppPicker, type TokenWithApp } from '../../../hooks/useAppPicker';
import { type AppPickerPageProps, type ViewTable } from '../../types';
import EmbeddedStatusScreen from './StatusScreen';
import { useEmbeddedCopy } from '../../../hooks/useEmbeddedCopy';
import { useQrOnScreen } from '../../../hooks/useQrOnScreen';

/** Tiles shown before "N more apps". Three fill the compact card exactly. */
const VISIBLE = 3;

type View = 'select' | 'more' | 'notListed';

/**
 * Choose: the first three apps, then "N more apps" (or "Don't see your app?"
 * when there are no more). All apps: the full list, scrolling past 440px.
 */
export default function EmbeddedAppPickerPage({
  tokens,
  handlerApps,
  recommendedHandlerAppUuids,
  onSelect,
}: AppPickerPageProps) {
  const { t, pick, org } = useEmbeddedCopy();
  const [view, setView] = useState<View>('select');
  // Back at the list, the next app starts on its own screen, not on the QR
  // code the last app showed in the drawer.
  const { setShown: setQrShown } = useQrOnScreen();
  useEffect(() => setQrShown(false), [setQrShown]);
  const picker = useAppPicker(
    tokens,
    handlerApps,
    recommendedHandlerAppUuids,
    onSelect,
  );

  const toItem = (item: TokenWithApp, tag?: AppTileTag) => ({
    key: item.token.payload.body.handlerAppUuid ?? item.name,
    name: item.name,
    logo: item.app?.base64Logo || undefined,
    tag,
    onSelect: () => picker.handleSelect(item.token),
  });
  type Item = ReturnType<typeof toItem>;

  const lastUsed: AppTileTag = {
    label: t('appPicker.sectionLastUsed'),
    variant: 'neutral',
  };
  const recommended: AppTileTag = {
    label: t('appPicker.sectionRecommended'),
    variant: 'secondary',
  };
  const apps = [
    ...(picker.lastUsed ? [toItem(picker.lastUsed, lastUsed)] : []),
    ...picker.recommended.map((item) => toItem(item, recommended)),
    ...picker.az.map((item) => toItem(item)),
  ];
  const list = (items: Item[]) => (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {items.map((app) => (
        <li key={app.key}>
          <AppTile
            name={app.name}
            logo={app.logo}
            tag={app.tag}
            size="compact"
            onClick={app.onSelect}
          />
        </li>
      ))}
    </ul>
  );
  const rest = apps.slice(VISIBLE);
  const notListed = {
    label: t('embedded.notListed.title'),
    onClick: () => setView('notListed'),
  };

  const views: ViewTable<View> = {
    select: () => (
      <EmbeddedCardLayout
        buttonRow={false}
        help={
          rest.length > 0 ? (
            <EmbeddedMoreAppsButton
              apps={rest}
              label={
                rest.length === 1
                  ? t('embedded.moreAppsOne')
                  : t('embedded.moreApps', { count: String(rest.length) })
              }
              onClick={() => setView('more')}
            />
          ) : (
            <EmbeddedNeedHelpSection links={[notListed]} />
          )
        }
      >
        {list(apps.slice(0, VISIBLE))}
      </EmbeddedCardLayout>
    ),
    more: () => (
      <EmbeddedCardLayout
        buttonRow={false}
        help={<EmbeddedNeedHelpSection links={[notListed]} />}
      >
        {list(apps)}
      </EmbeddedCardLayout>
    ),
    notListed: () => (
      <EmbeddedStatusScreen
        visual="help"
        title={t('embedded.notListed.title')}
        sub={t(
          pick('embedded.notListed.sub', 'embedded.notListed.subIssuance'),
          {
            org,
          },
        )}
        secondary={{
          label: t('embedded.notListed.back'),
          onClick: () => setView('select'),
        }}
      />
    ),
  };

  return views[view]();
}
